/**
 * [INPUT]: 依赖 react；依赖 ./Uploader ./Footer；依赖 ../seo/faq-items 的 FAQ_ITEMS；依赖 ../pipeline/mediapipe 的 preloadModel
 * [OUTPUT]: 对外提供 LandingPage 组件（落地页 hero + 功能 + 流程 + PSL 量表 + 示例结果 + 改进指南 + FAQ）
 * [POS]: apps/web/components 的营销落地页，由 ScoringFlow 在 idle/loading 状态挂载；即 / 路由的预渲染快照
 *   - head/JSON-LD 不再由此组件注入：路由级 meta/links 由 app/routes/_index.tsx 在构建期经 <Meta/> 注入
 * [PROTOCOL]: 变更时更新本头部，然后检查 ../CLAUDE.md
 */
import { useEffect, useState } from 'react'
import { Uploader } from './Uploader'
import { Footer } from './Footer'
import { FAQ_ITEMS } from '../seo/faq-items'
import { preloadModel } from '../pipeline/mediapipe'
import { trackEvent } from '../analytics'

interface Props {
  onFile: (file: File) => void
  loading: boolean
}

const TIERS = [
  { range: '7.5 - 8.0', name: 'Adam / Eve', desc: 'Near-perfect bone structure, exceptionally rare (supermodel / icon tier)', color: 'text-olive bg-olive/10 border-olive/20' },
  { range: '6.9 - 7.4', name: 'Chad / Stacy', desc: 'Powerful facial appeal with outstanding jaw and cheekbone definition', color: 'text-olive bg-olive/10 border-olive/20' },
  { range: '6.0 - 6.8', name: 'Chadlite / Stacylite', desc: 'Model-tier — well-defined bone structure with excellent symmetry', color: 'text-brass bg-brass/10 border-brass/20' },
  { range: '5.0 - 5.9', name: 'HTN (High Tier Normie)', desc: 'Noticeably attractive with balanced proportions and no major flaws', color: 'text-brass bg-brass/10 border-brass/20' },
  { range: '4.0 - 4.9', name: 'Normie', desc: 'Population average — facial proportions largely within normal range', color: 'text-amber bg-amber/10 border-amber/20' },
  { range: '0.0 - 3.9', name: 'Below Average', desc: 'Below the population mean; may reflect proportion imbalance or poor photo conditions', color: 'text-terracotta bg-terracotta/10 border-terracotta/20' },
]

/** Anonymized, illustrative result breakdowns (not real users) for trust + content thickness. */
const EXAMPLE_RESULTS = [
  {
    total: 4.2,
    tier: 'Normie',
    accent: 'amber',
    categories: { harmony: 4.5, dimorphism: 4.0, angularity: 3.8, presentation: 4.4 },
    takeaway: 'Balanced and right around the population mean — no major structural weaknesses.',
  },
  {
    total: 5.3,
    tier: 'HTN',
    accent: 'brass',
    categories: { harmony: 5.4, dimorphism: 5.1, angularity: 5.6, presentation: 5.0 },
    takeaway: 'Noticeably attractive with strong jaw definition and clean facial thirds.',
  },
  {
    total: 6.5,
    tier: 'Chadlite',
    accent: 'olive',
    categories: { harmony: 6.6, dimorphism: 6.2, angularity: 6.8, presentation: 6.3 },
    takeaway: 'Model-tier bone structure — sharp gonial angle and excellent symmetry.',
  },
] as const

type ExampleAccent = (typeof EXAMPLE_RESULTS)[number]['accent']

/**
 * Full literal class strings per accent — Tailwind JIT only detects complete class names,
 * so dynamic `text-${accent}` interpolation would be purged. Keep these verbatim.
 */
const ACCENT_CLASSES: Record<ExampleAccent, { text: string; badge: string; bar: string }> = {
  amber: { text: 'text-amber', badge: 'bg-amber/10 text-amber border-amber/20', bar: 'bg-amber' },
  brass: { text: 'text-brass', badge: 'bg-brass/10 text-brass border-brass/20', bar: 'bg-brass' },
  olive: { text: 'text-olive', badge: 'bg-olive/10 text-olive border-olive/20', bar: 'bg-olive' },
}

const CATEGORY_LABELS: Record<keyof (typeof EXAMPLE_RESULTS)[number]['categories'], string> = {
  harmony: 'Harmony',
  dimorphism: 'Dimorphism',
  angularity: 'Angularity',
  presentation: 'Presentation',
}

const IMPROVE_TIPS = [
  {
    title: 'Lower body fat',
    body: 'Reducing body fat reveals jawline and cheekbone angularity — the single most impactful lever for the Angularity and Dimorphism dimensions.',
  },
  {
    title: 'Tongue & neck posture',
    body: 'Resting tongue-on-palate (mewing) and a neutral neck align the jaw and soft-tissue contours over time, supporting jaw definition.',
  },
  {
    title: 'Skincare & grooming',
    body: 'A consistent skincare, grooming, and sleep routine lifts the Presentation score and reduces asymmetry from puffiness.',
  },
  {
    title: 'Hairstyle framing',
    body: 'Choose a hairstyle that balances your facial thirds and frames the jawline — bangs or an exposed forehead visually shift proportion.',
  },
  {
    title: 'Eye-level, even lighting',
    body: 'Shoot front-facing at eye level under soft bilateral light. This both improves analysis accuracy and avoids penalizing Presentation.',
  },
  {
    title: 'Posture & symmetry',
    body: 'Address habitual head tilt and uneven chewing — mild asymmetry is often postural and improves with conscious correction.',
  },
] as const

/** 8 equal-width segments (each 1.0 PSL) colored by tier — the visual PSL rating chart. */
const SCALE_SEGMENTS = [
  { color: 'bg-terracotta', label: 'Below avg' },
  { color: 'bg-terracotta', label: '' },
  { color: 'bg-terracotta', label: '' },
  { color: 'bg-terracotta', label: '' },
  { color: 'bg-amber', label: 'Normie' },
  { color: 'bg-brass', label: 'HTN' },
  { color: 'bg-brass', label: 'Chadlite' },
  { color: 'bg-olive', label: 'Chad+' },
] as const

const NAV_LINKS = [
  { href: '#features', label: 'Features' },
  { href: '#how-it-works', label: 'How it works' },
  { href: '#scale', label: 'PSL scale' },
  { href: '#examples', label: 'Examples' },
  { href: '#improve', label: 'Improve' },
  { href: '#faq', label: 'FAQ' },
] as const

export function LandingPage({ onFile, loading }: Props) {
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  // 空闲预热模型：把首次 WASM+模型下载藏进用户阅读落地页的时间内，上传后即用。
  useEffect(() => {
    const start = () => {
      preloadModel()
    }
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback
    if (typeof ric === 'function') {
      ric(start, { timeout: 2500 })
    } else {
      window.setTimeout(start, 1200)
    }
  }, [])

  function toggleFaq(index: number) {
    const next = openFaq === index ? null : index
    if (next !== null && FAQ_ITEMS[index]) {
      trackEvent('FAQ Toggle', { index, question: FAQ_ITEMS[index].q })
    }
    setOpenFaq(next)
  }

  function goHome() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col antialiased selection:bg-brass selection:text-slate-950">
      {/* Background Gradients (LUME Warm Brass & Amber) */}
      <div className="absolute top-0 left-0 right-0 h-[650px] pointer-events-none overflow-hidden opacity-25">
        <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-brass/25 blur-[130px]" />
        <div className="absolute top-[-5%] right-[-10%] w-[500px] h-[500px] rounded-full bg-amber/15 blur-[120px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-slate-950/75 border-b border-slate-700 transition-all duration-300">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <button onClick={goHome} className="flex items-center gap-2.5 shrink-0">
            <img
              src="/logo-mark.svg"
              alt=""
              className="w-8 h-8 rounded-lg shadow-lg shadow-brass/20"
              aria-hidden="true"
            />
            <span className="font-bold text-lg tracking-tight bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">
              PSL Analyzer
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-5 text-xs font-semibold text-slate-400" aria-label="Page sections">
            {NAV_LINKS.map((link) => (
              <a key={link.href} href={link.href} className="hover:text-white transition duration-200">
                {link.label}
              </a>
            ))}
          </nav>

          <div className="shrink-0">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-brass/10 text-brass border border-brass/20">
              <span className="w-1.5 h-1.5 rounded-full bg-brass animate-pulse" />
              100% Local Privacy
            </span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative max-w-4xl w-full mx-auto px-6 pt-16 pb-20 flex-grow flex flex-col items-center text-center space-y-8">
        {/* Pill Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/80 border border-slate-700 text-xs font-semibold text-brass tracking-wider uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-amber animate-pulse" />
          Free PSL Scale Test · 1–8 Rating
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-sans font-light tracking-tight leading-tight text-white max-w-3xl">
          Free{' '}
          <span className="bg-gradient-to-r from-brass via-amber to-gold bg-clip-text text-transparent italic px-1 font-sans font-extrabold">
            PSL Scale
          </span>{' '}
          Test
        </h1>

        {/* Descriptions */}
        <div className="space-y-4 max-w-2xl text-slate-400 text-sm md:text-base leading-relaxed">
          <p>
            The <span className="text-slate-300 font-medium">PSL Scale</span> is a 0–8 facial rating system from the looksmaxxing community.
          </p>
          <p>
            Upload a front-facing photo and get your accurate PSL score instantly — 100% on-device, no upload.
          </p>
        </div>

        {/* Feature Pills */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs font-medium text-slate-400">
          <div className="flex items-center gap-1.5">
            <span className="text-brass">●</span> No Sign-up 
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-amber">●</span> Your Photo Never Leaves Your Device
          </div>
        </div>

        {/* Tool Box (Uploader Card) */}
        <div id="upload" className="w-full max-w-md pt-6">
          <div className="w-full backdrop-blur-xl bg-slate-900/40 border border-slate-700 rounded-3xl p-6 md:p-8 shadow-2xl shadow-black/40 hover:border-slate-700 transition-colors duration-300">
            <Uploader
              onFile={onFile}
              loading={loading}
            />
          </div>
        </div>
      </section>

      {/* Core Advantages */}
      <section id="features" className="border-t border-slate-700 bg-slate-950 py-20 relative scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center space-y-3 mb-16">
            <h2 className="text-3xl font-light tracking-tight text-white">Why Choose PSL Analyzer</h2>
            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              No black-box algorithms. No subjective bias. Pure geometric measurement — open, transparent, and fully on-device.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group rounded-2xl border border-slate-800 bg-slate-900/20 p-6 hover:border-slate-700/60 hover:bg-slate-900/30 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-brass/10 text-brass flex items-center justify-center mb-5 border border-brass/20 group-hover:bg-brass group-hover:text-slate-950 transition-all duration-300" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Absolute On-Device Privacy</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Every step runs 100% on your device — from image decoding to facial analysis. No cloud server is involved at any point, and your original photo is never uploaded.
              </p>
            </div>

            <div className="group rounded-2xl border border-slate-800 bg-slate-900/20 p-6 hover:border-slate-700/60 hover:bg-slate-900/30 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-amber/10 text-amber flex items-center justify-center mb-5 border border-amber/20 group-hover:bg-amber group-hover:text-slate-950 transition-all duration-300" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 19.5H4.25a2.25 2.25 0 01-2.25-2.25V3a.75.75 0 011.35-.45l18.75 16.5a.75.75 0 01-.35.45z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 16.5V7.5L16.5 16.5H6z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Objective Bone & Proportion Analysis</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Grounded in classical aesthetic ratios (facial thirds, golden proportion) and craniofacial parameters (gonial angle, canthal tilt), our engine outputs a purely geometry-driven score — free from subjective interpretation.
              </p>
            </div>

            <div className="group rounded-2xl border border-slate-800 bg-slate-900/20 p-6 hover:border-slate-700/60 hover:bg-slate-900/30 transition-all duration-300">
              <div className="w-10 h-10 rounded-xl bg-gold/10 text-gold flex items-center justify-center mb-5 border border-gold/20 group-hover:bg-gold group-hover:text-slate-950 transition-all duration-300" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Multi-Dimensional Breakdown</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Your report breaks the total score into 4 categories and 8 sub-indicators — identifying strengths and weaknesses with explainable deltas, plus non-invasive, science-based improvement suggestions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="border-t border-slate-700 bg-slate-950/60 py-20 relative scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center space-y-3 mb-16">
            <h2 className="text-3xl font-light tracking-tight text-white">How It Works</h2>
            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              Our on-device AI measures your facial structure, eye area, midface, and jawline locally in your browser, then calculates your precise PSL score and tier on the official 0–8 scale — nothing is uploaded to any server.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="relative space-y-3">
              <div className="text-slate-700 text-5xl font-extrabold select-none" aria-hidden="true">01</div>
              <h3 className="text-base font-bold text-white">Upload a Front-Facing Photo</h3>
              <p className="text-slate-400 text-xs leading-relaxed">
                Upload a clear, eye-level front-facing portrait with even lighting and a natural expression. Gender parameters default to neutral — no manual selection required.
              </p>
            </div>

            <div className="relative space-y-3">
              <div className="text-slate-700 text-5xl font-extrabold select-none" aria-hidden="true">02</div>
              <h3 className="text-base font-bold text-white">On-Device Facial Analysis</h3>
              <p className="text-slate-400 text-xs leading-relaxed">
                Your photo is analyzed entirely in your browser — facial geometry and proportions are measured locally in milliseconds, without any server round-trip or upload.
              </p>
            </div>

            <div className="relative space-y-3">
              <div className="text-slate-700 text-5xl font-extrabold select-none" aria-hidden="true">03</div>
              <h3 className="text-base font-bold text-white">Geometric Feature Extraction</h3>
              <p className="text-slate-400 text-xs leading-relaxed">
                From your facial geometry, we compute key proportions and angles: facial thirds ratio, midface ratio, interpupillary distance, canthal tilt, gonial angle, and more — all dimensionless, resolution-independent measurements.
              </p>
            </div>

            <div className="relative space-y-3">
              <div className="text-slate-700 text-5xl font-extrabold select-none" aria-hidden="true">04</div>
              <h3 className="text-base font-bold text-white">Score & Recommendation</h3>
              <p className="text-slate-400 text-xs leading-relaxed">
                Measured values are mapped to the PSL Scale and assigned a tier. Strengths and improvement areas are identified, producing a complete analysis card with grooming, styling, and skincare suggestions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PSL Scale */}
      <section id="scale" className="border-t border-slate-700 bg-slate-950 py-20 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Visual PSL rating chart (0–8 gradient scale) */}
          <div className="text-center space-y-3 mb-14">
            <h2 className="text-3xl font-light tracking-tight text-white">PSL Rating Scale Explained (0–8)</h2>
            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              The PSL Scale runs 0–8 around a strict mean of 4.0. Most people cluster between 3.5 and 5.0 — each 0.5 point is a meaningful percentile jump.
            </p>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/20 p-6 md:p-8 mb-14">
            {/* Segmented bar */}
            <div className="flex h-12 w-full rounded-lg overflow-hidden border border-slate-800">
              {SCALE_SEGMENTS.map((seg, i) => (
                <div
                  key={i}
                  className={`flex-1 ${seg.color} relative flex items-end justify-center pb-1`}
                  aria-hidden="true"
                >
                  <span className="text-[10px] font-bold text-slate-950/80">{i}</span>
                </div>
              ))}
            </div>
            {/* Tier labels under segments */}
            <div className="flex w-full mt-2 text-[10px] font-semibold text-slate-400">
              {SCALE_SEGMENTS.map((seg, i) => (
                <div key={i} className="flex-1 text-center leading-tight min-h-[24px]">
                  {seg.label}
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3 text-center">
              Bell-curve distributed: <span className="text-terracotta">0–3.9</span> below average ·{' '}
              <span className="text-amber">4.0–4.9</span> Normie · <span className="text-brass">5.0–6.8</span> HTN / Chadlite ·{' '}
              <span className="text-olive">6.9–8.0</span> Chad / Adam &amp; Eve
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Info */}
            <div className="lg:col-span-5 space-y-5 text-center lg:text-left">
              <h2 className="text-3xl font-light tracking-tight text-white">What is the PSL Scale?</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                The PSL Scale — Perceived Sexual Level — is a deliberately strict, normally-distributed facial attractiveness framework with a fixed mean of 4.0. Unlike subjective 1–10 rating apps, the PSL Scale is grounded in craniofacial proportions: facial harmony, sexual dimorphism, bone angularity, and presentation. A PSL score tells you where you sit on the 0–8 scale relative to the population, not a vanity number.
              </p>
              <div className="rounded-2xl border border-slate-800 bg-slate-900/30 p-5 text-xs text-slate-400 leading-relaxed max-w-md mx-auto lg:mx-0">
                <span className="font-bold text-white block mb-1">Did You Know</span>
                PSL Scale scores follow a standard bell curve. Each 0.5-point gain represents a significant jump in population percentile. A PSL of 4.0–4.5 already places you among the well-proportioned majority.
              </div>
            </div>

            {/* Right Table */}
            <div className="lg:col-span-7 rounded-2xl border border-slate-800 bg-slate-900/20 overflow-hidden shadow-xl">
              <div className="px-6 py-4 border-b border-slate-800 bg-slate-900/40 text-xs font-bold uppercase tracking-wider text-slate-400">
                PSL Tier Reference Chart
              </div>
              <div className="divide-y divide-slate-800/80">
                {TIERS.map((t) => (
                  <div key={t.name} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 hover:bg-slate-900/10 transition-colors duration-150">
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold border ${t.color}`}>
                        {t.range}
                      </span>
                      <span className="font-semibold text-white text-sm">{t.name}</span>
                    </div>
                    <span className="text-xs text-slate-400 sm:text-right">{t.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Example PSL Analyses — illustrative, anonymized breakdowns for trust + dwell */}
      <section id="examples" className="border-t border-slate-700 bg-slate-950/60 py-20 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center space-y-3 mb-16">
            <h2 className="text-3xl font-light tracking-tight text-white">See Example PSL Analyses</h2>
            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              Illustrative, fully anonymized breakdowns across the four PSL dimensions — so you know exactly what a result looks like before you upload.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {EXAMPLE_RESULTS.map((ex) => {
              const accent = ACCENT_CLASSES[ex.accent]
              return (
                <div
                  key={ex.total}
                  className="rounded-2xl border border-slate-800 bg-slate-900/20 p-6 flex flex-col gap-5 hover:border-slate-700/60 transition-colors duration-300"
                >
                  {/* Score head */}
                  <div className="flex items-baseline justify-between">
                    <div className="flex items-baseline gap-1.5">
                      <span className={`text-5xl font-extrabold tracking-tight ${accent.text}`}>{ex.total.toFixed(1)}</span>
                      <span className="text-slate-500 text-sm font-medium">/ 8</span>
                    </div>
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-bold border ${accent.badge}`}>
                      {ex.tier}
                    </span>
                  </div>

                  {/* Category breakdown bars */}
                  <div className="space-y-2.5">
                    {(Object.keys(CATEGORY_LABELS) as (keyof typeof CATEGORY_LABELS)[]).map((key) => {
                      const v = ex.categories[key]
                      return (
                        <div key={key} className="space-y-1">
                          <div className="flex justify-between text-[11px] text-slate-400">
                            <span>{CATEGORY_LABELS[key]}</span>
                            <span className="tabular-nums text-slate-300">{v.toFixed(1)}</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${accent.bar}`}
                              style={{ width: `${(v / 8) * 100}%` }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <p className="text-xs text-slate-400 leading-relaxed mt-auto">{ex.takeaway}</p>
                </div>
              )
            })}
          </div>

          <p className="text-center text-[11px] text-slate-600 mt-6">
            Illustrative examples only — not real user data. Your actual score is computed from your unique facial geometry.
          </p>
        </div>
      </section>

      {/* How to Improve Your PSL Score — natural, non-surgical levers */}
      <section id="improve" className="border-t border-slate-700 bg-slate-950 py-20 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center space-y-3 mb-16">
            <h2 className="text-3xl font-light tracking-tight text-white">How to Improve Your PSL Score</h2>
            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              PSL measures mostly structural geometry, but several natural, non-surgical levers can meaningfully shift your dimensions and your photo quality.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {IMPROVE_TIPS.map((tip, i) => (
              <div
                key={tip.title}
                className="rounded-2xl border border-slate-800 bg-slate-900/20 p-6 hover:border-slate-700/60 transition-colors duration-300"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-7 h-7 rounded-lg bg-brass/10 text-brass flex items-center justify-center text-xs font-bold border border-brass/20">
                    {i + 1}
                  </span>
                  <h3 className="text-base font-bold text-white">{tip.title}</h3>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">{tip.body}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <a
              href="#upload"
              onClick={() => trackEvent('Hero CTA Click', { location: 'improve_section' })}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-brass to-amber text-slate-950 text-sm font-bold hover:opacity-90 transition-opacity duration-200"
            >
              Get my PSL score →
            </a>
          </div>
        </div>
      </section>

      {/* FAQ — answers always in DOM for crawlability; expand/collapse is visual only */}
      <section id="faq" className="border-t border-slate-700 bg-slate-950/60 py-20 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center space-y-3 mb-16">
            <h2 className="text-3xl font-light tracking-tight text-white">Frequently Asked Questions</h2>
            <p className="text-slate-400 text-sm">
              Detailed answers about our on-device architecture, privacy guarantees, and scoring accuracy.
            </p>
          </div>

          <div className="space-y-4">
            {FAQ_ITEMS.map((item, idx) => {
              const isOpen = openFaq === idx
              return (
                <div
                  key={item.q}
                  className="rounded-2xl border border-slate-800 bg-slate-900/10 overflow-hidden hover:border-slate-700/80 transition-all duration-300"
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(idx)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-panel-${idx}`}
                    id={`faq-trigger-${idx}`}
                    className="w-full px-6 py-5 flex items-center justify-between text-left text-sm md:text-base font-bold text-white hover:bg-slate-900/20 transition-colors duration-150"
                  >
                    <span>{item.q}</span>
                    <span
                      className={`text-slate-500 font-normal transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    >
                      ▼
                    </span>
                  </button>

                  <div
                    id={`faq-panel-${idx}`}
                    role="region"
                    aria-labelledby={`faq-trigger-${idx}`}
                    // Answer text stays mounted so crawlers always see FAQ copy after JS render.
                    className={
                      isOpen
                        ? 'px-6 pb-5 pt-4 text-xs md:text-sm text-slate-400 leading-relaxed border-t border-slate-800 bg-slate-950/40'
                        : 'sr-only'
                    }
                  >
                    <p>{item.a}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  )
}
